'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { Check, Flag, Lock, Mail, Phone, Send, UserRound, X } from 'lucide-react'
import { Button } from '@/components/elements/Button'
import Textarea from '@/components/elements/Textarea'
import { Label } from '@/components/ui/label'
import { useVendorReport } from '@/lib/commerce/hooks'
import { REPORT_REASONS, REPORT_MESSAGE_MAX, type ReportReasonTone } from '@/utils/report'

const tones: Record<ReportReasonTone, { ic: string, bg: string, on: string }> = {
  red: { ic: 'text-red-600', bg: 'bg-red-50', on: 'border-red-500 bg-red-50/60' },
  amber: { ic: 'text-amber-600', bg: 'bg-amber-50', on: 'border-amber-500 bg-amber-50/60' },
  slate: { ic: 'text-slate-600', bg: 'bg-slate-100', on: 'border-slate-500 bg-slate-50' },
}

interface ReportVendorDialogProps {
  open: boolean
  onClose: () => void
  vendorId: string
  vendorName?: string
}

export default function ReportVendorDialog({ open, onClose, vendorId, vendorName }: ReportVendorDialogProps) {
  const r = useVendorReport(vendorId, onClose)

  const handleClose = () => {
    if (r.submitting) return
    onClose()
    r.reset()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await r.submit()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 sm:scale-[0.985]" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-2 sm:scale-[0.985]">
              <DialogPanel className="w-full max-w-[560px] max-h-[90vh] sm:max-h-[85vh] bg-white rounded-t-2xl sm:rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] overflow-hidden flex flex-col">
                <div className="relative px-6 pt-6 pb-5 border-b border-border shrink-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-red-50 border border-red-100 grid place-items-center text-red-600 shrink-0">
                        <Flag className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[16px] font-semibold leading-tight tracking-[-0.005em]">Дэлгүүрийг мэдэгдэх</div>
                        <div className="mt-1 text-[12.5px] text-muted-foreground leading-snug">
                          {vendorName ? <><span className="font-semibold text-foreground">{vendorName}</span> дэлгүүрийн талаар мэдээлэл өгнө үү.</> : 'Дэлгүүрийн талаар мэдээлэл өгнө үү. Shop.mn багийнхан хянана.'}
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={handleClose} className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-muted transition-colors shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-5 overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom))]">
                  <div className="mb-5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Label>Шалтгаан <span className="text-red-500 -ml-1">*</span></Label>
                      <span className="text-[11px] font-normal text-muted-foreground ml-auto">{r.reason ? '1 сонгосон' : 'Аль нэгийг сонго'}</span>
                    </div>
                    <div className="space-y-2">
                      {REPORT_REASONS.map(item => {
                        const active = r.reason === item.value
                        const t = tones[item.tone]
                        return (
                          <button type="button" key={item.value} onClick={() => r.setReason(item.value)} className={`w-full text-left rounded-xl border-2 px-3.5 py-3 transition-all flex items-start gap-3 cursor-pointer ${active ? `${t.on} shadow-sm` : 'border-border hover:border-foreground/30 bg-white'}`}>
                            <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${t.bg} ${t.ic}`}>
                              <item.Icon className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13.5px] font-semibold leading-tight">{item.label}</div>
                              <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">{item.sub}</div>
                            </div>
                            <div className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 mt-0.5 ${active ? 'border-foreground bg-foreground' : 'border-border'}`}>
                              {active && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <Textarea label="Юу болсныг тайлбарлана уу *" value={r.message} onChange={(e: any) => r.setMessage(e.target.value.slice(0, REPORT_MESSAGE_MAX))} rows={4} placeholder="Жишээ: Бараагаа захиалаад төлбөрөө шилжүүлсэн боловч хариу ирэхгүй 5 хоног боллоо. Утсаар холбогдохгүй..." description={`${r.message.length}/${REPORT_MESSAGE_MAX}`} />
                    <span className="block mt-1.5 text-xs text-muted-foreground">Захиалгын дугаар, огноо, мөнгөн дүн зэрэг тодорхой нотолгоо хавсаргахыг зөвлөе.</span>
                  </div>

                  <div className="border-t border-border mt-6 my-4"></div>

                  {/* <div className="mb-4 rounded-xl border border-border overflow-hidden"> */}
                  <div className="mb-4 overflow-hidden">
                    {/* <div className="flex items-center gap-2.5 px-3.5 py-3"> */}
                    <div className="flex items-center gap-2.5 py-1.5">
                      <UserRound className="h-4 w-4 text-muted-foreground" />
                      <Label>Холбоо барих мэдээлэл <span className="text-red-500 -ml-1">*</span></Label>
                      <span className="text-[10.5px] font-medium tracking-[0.05em] uppercase text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Заавал</span>
                    </div>
                    {/* <div className="px-3.5 pb-3.5 pt-1 space-y-4"> */}
                    <div className="pb-3.5 pt-1 space-y-4">
                      <div className="text-xs text-muted-foreground leading-snug">Мэдэгдлийг шалгахад бид тантай холбогдох тул мэдээллээ бүрэн бөглөнө үү.</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <DialogField label="Нэр" icon={UserRound} value={r.guestName} onChange={r.setGuestName} placeholder="Хэрэглэгч" />
                        <DialogField label="Утас" icon={Phone} value={r.guestPhone} onChange={r.setGuestPhone} placeholder="80000000" />
                      </div>
                      <DialogField label="Имэйл" icon={Mail} type="email" value={r.guestEmail} onChange={r.setGuestEmail} placeholder="user@shop.mn" />
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-xs text-muted-foreground leading-snug">
                    <Lock className="h-3.5 w-3.5 mt-px shrink-0" />
                    <span>Мэдэгдэлд явуулсан холбоо барих мэдээллийг нууцлах ба зөвхөн Shop.mn-д мэдэгдэх болно.</span>
                  </div>

                  <div className="mt-5 flex items-center justify-end gap-2">
                    <Button type="button" variant="outline" onClick={handleClose} disabled={r.submitting}>Болих</Button>
                    <Button type="submit" variant="destructive" icon={Send} isLoading={r.submitting} loadingMessage="Илгээж байна..." className="text-white!">Мэдэгдэл илгээх</Button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

function DialogField({ label, icon: Icon, value, onChange, placeholder, type = 'text' }: { label: string, icon: any, value: string, onChange: (v: string) => void, placeholder?: string, type?: string }) {
  return (
    <div className="grid w-full items-center gap-1.5">
      <Label className="mb-1">{label} <span className="text-red-500 -ml-1">*</span></Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full h-10 text-[13px] rounded-md border border-border bg-white outline-none focus:border-foreground/50 focus:ring-2 focus:ring-foreground/10 pl-9 pr-3" />
      </div>
    </div>
  )
}
